import React, { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react';
import {
  aiSettingsApi,
  ChangeNote,
  ProviderKey,
  ProviderStatus,
  RoutingState,
} from '../../api/ai-settings';
import { useToast } from '../../hooks/useToast';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { move, sameRouting } from './format';

interface Props {
  routing: RoutingState;
  providers: ProviderStatus[];
  canManage: boolean;
  onChanged: () => void;
}

const muted: React.CSSProperties = { fontSize: 13, color: 'var(--neutral-600)' };

/**
 * Which provider answers first, and who covers when it is down.
 *
 * Only vendors that are approved *and* configured can be chosen: approval is
 * the compliance gate (a deploy setting), and the server refuses anything
 * else, so the screen does not offer it.
 */
export const RoutingTab: React.FC<Props> = ({ routing, providers, canManage, onChanged }) => {
  const toast = useToast();
  const [def, setDef] = useState<ProviderKey>(routing.defaultProvider);
  const [order, setOrder] = useState<ProviderKey[]>(routing.fallbackOrder);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState<ChangeNote[]>([]);

  useEffect(() => {
    setDef(routing.defaultProvider);
    setOrder(routing.fallbackOrder);
  }, [routing.defaultProvider, routing.fallbackOrder]);

  const byKey = useMemo(
    () => Object.fromEntries(providers.map((p) => [p.key, p])) as Record<ProviderKey, ProviderStatus>,
    [providers],
  );
  const usable = (k: ProviderKey) => byKey[k]?.approved && byKey[k]?.configured;
  const reason = (p: ProviderStatus) =>
    !p.approved ? 'Not approved' : !p.configured ? 'Not configured on the server' : null;

  const addable = providers.filter((p) => usable(p.key) && p.key !== def && !order.includes(p.key));
  const dirty = !sameRouting({ defaultProvider: def, fallbackOrder: order }, routing);

  const choose = (k: ProviderKey) => {
    setDef(k);
    setOrder((o) => o.filter((x) => x !== k));
  };

  const save = async () => {
    setBusy(true);
    try {
      const out = await aiSettingsApi.setRouting({ defaultProvider: def, fallbackOrder: order });
      setNotes(out.notes ?? []);
      toast.success('Routing saved. It applies within seconds.');
      onChanged();
    } catch (err) {
      toast.error(err, 'Could not save routing.');
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    try {
      await aiSettingsApi.clearRouting();
      setNotes([]);
      toast.success('Back to the deploy settings.');
      onChanged();
    } catch (err) {
      toast.error(err, 'Could not reset routing.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Card>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Default provider</h2>
        <p style={{ ...muted, marginBottom: 16 }}>
          Answers every AI request first. If it fails or is slow, the fallbacks below are tried
          in order.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
          {providers.map((p) => {
            const r = reason(p);
            const selected = def === p.key;
            return (
              <label
                key={p.key}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  padding: 14,
                  borderRadius: 'var(--radius-md)',
                  border: `1px solid ${selected ? 'var(--blue-600)' : 'var(--neutral-300)'}`,
                  background: selected ? 'var(--blue-50)' : 'transparent',
                  opacity: r ? 0.6 : 1,
                  cursor: r || !canManage ? 'not-allowed' : 'pointer',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
                  <input
                    type="radio"
                    name="default-provider"
                    checked={selected}
                    disabled={Boolean(r) || !canManage}
                    onChange={() => choose(p.key)}
                    aria-label={`Default: ${p.label}`}
                  />
                  {p.label}
                </span>
                <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <Badge variant={p.approved ? 'success' : 'neutral'}>
                    {p.approved ? 'Approved' : 'Not approved'}
                  </Badge>
                  <Badge variant={p.configured ? 'success' : 'neutral'}>
                    {p.configured ? 'Configured' : 'Not configured'}
                  </Badge>
                </span>
                <span style={muted}>
                  {p.visionEnabled ? 'Can read images' : 'No images'}
                  {p.position ? ` · position ${p.position} in the chain` : ''}
                </span>
                {r && <span style={{ ...muted, color: 'var(--neutral-500)' }}>{r}</span>}
              </label>
            );
          })}
        </div>
      </Card>

      <Card>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Fallback order</h2>
        <p style={{ ...muted, marginBottom: 16 }}>
          Tried top to bottom when the one before it is down. A provider that keeps failing is
          skipped for 30 seconds, then tried once.
        </p>
        {order.length === 0 ? (
          <p style={muted}>No fallbacks: if {byKey[def]?.label ?? def} is down, AI features degrade.</p>
        ) : (
          <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {order.map((k, i) => (
              <li
                key={k}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 14px',
                  border: '1px solid var(--neutral-300)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <span style={{ width: 22, color: 'var(--neutral-500)', fontSize: 13 }}>{i + 1}</span>
                <span style={{ fontWeight: 500, flex: 1 }}>{byKey[k]?.label ?? k}</span>
                {canManage && (
                  <span style={{ display: 'flex', gap: 4 }}>
                    <Button size="sm" variant="ghost" aria-label={`Move ${k} up`} disabled={i === 0} onClick={() => setOrder((o) => move(o, i, i - 1))}>
                      <ArrowUp size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" aria-label={`Move ${k} down`} disabled={i === order.length - 1} onClick={() => setOrder((o) => move(o, i, i + 1))}>
                      <ArrowDown size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" aria-label={`Remove ${k}`} onClick={() => setOrder((o) => o.filter((x) => x !== k))}>
                      <X size={14} />
                    </Button>
                  </span>
                )}
              </li>
            ))}
          </ol>
        )}
        {canManage && addable.length > 0 && (
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            {addable.map((p) => (
              <Button key={p.key} size="sm" variant="secondary" onClick={() => setOrder((o) => [...o, p.key])}>
                <Plus size={14} /> {p.label}
              </Button>
            ))}
          </div>
        )}
      </Card>

      {notes.length > 0 && (
        <div role="status" style={{ ...muted, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {notes.map((n) => (
            <span key={n.message}>{n.message}</span>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        {canManage && (
          <>
            <Button onClick={save} disabled={!dirty || busy} loading={busy}>
              Save routing
            </Button>
            {routing.fromAdmin && (
              <Button variant="secondary" onClick={reset} disabled={busy}>
                Reset to deploy settings
              </Button>
            )}
          </>
        )}
        <span style={muted}>
          {routing.fromAdmin
            ? 'Set here.'
            : `Using the deploy settings (${routing.deploy.defaultProvider}${routing.deploy.fallbackOrder.length ? ' → ' + routing.deploy.fallbackOrder.join(' → ') : ''}).`}{' '}
          Approving a vendor and API keys are deploy settings, not editable here.
        </span>
      </div>
    </div>
  );
};
